/*
 * <zoom-image> — zoomable image used inside the fullscreen product gallery modal.
 *
 * Opens fit-to-screen; zoom in/out via the +/- buttons (zoomIn/zoomOut API),
 * scroll wheel (toward the cursor), or two-finger pinch on touch. Drag (mouse or
 * one finger) pans while zoomed; double-click toggles 1x/2.5x. Panning is clamped
 * so the image can't be dragged off-screen.
 */
if (!customElements.get('zoom-image')) {
  customElements.define(
    'zoom-image',
    class ZoomImage extends HTMLElement {
      MIN = 1;
      MAX = 5;
      STEP = 1.4;

      connectedCallback() {
        this.image = this.querySelector('img');
        if (!this.image) return;

        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.pointers = new Map();
        this.pinchStartDistance = 0;
        this.pinchStartScale = 1;
        this.dragStart = null;

        this.lastPointerType = 'mouse';
        this.dragged = false;

        ['onWheel', 'onClick', 'onDblClick', 'onPointerDown', 'onPointerMove', 'onPointerUp'].forEach(
          (fn) => (this[fn] = this[fn].bind(this))
        );

        this.addEventListener('wheel', this.onWheel, { passive: false });
        this.addEventListener('click', this.onClick);
        this.addEventListener('dblclick', this.onDblClick);
        this.addEventListener('pointerdown', this.onPointerDown);
        this.addEventListener('pointermove', this.onPointerMove, { passive: false });
        this.addEventListener('pointerup', this.onPointerUp);
        this.addEventListener('pointercancel', this.onPointerUp);
      }

      get isZoomed() {
        return this.scale > 1.01;
      }

      clamp(value, min, max) {
        return Math.min(max, Math.max(min, value));
      }

      // Keep the scaled image within the viewport bounds (the image is letterboxed
      // by object-fit, so clamp against its own box, not the container).
      clampPan() {
        const overflowX = Math.max(0, (this.image.offsetWidth * this.scale - this.clientWidth) / 2);
        const overflowY = Math.max(0, (this.image.offsetHeight * this.scale - this.clientHeight) / 2);
        this.tx = this.clamp(this.tx, -overflowX, overflowX);
        this.ty = this.clamp(this.ty, -overflowY, overflowY);
      }

      apply() {
        this.clampPan();
        this.image.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
        this.classList.toggle('is-zoomed', this.isZoomed);
        // While zoomed, claim all touch gestures so dragging pans (instead of swiping slides).
        this.style.touchAction = this.isZoomed || this.classList.contains('is-pinching') ? 'none' : '';
        if (!this.dragStart) this.style.cursor = this.isZoomed ? 'grab' : '';
      }

      reset() {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.classList.remove('is-pinching');
        this.apply();
      }

      // Zoom toward a focal point (client coords); defaults to the centre.
      zoomTo(nextScale, clientX, clientY) {
        const rect = this.getBoundingClientRect();
        const focalX = (clientX == null ? rect.left + rect.width / 2 : clientX) - (rect.left + rect.width / 2);
        const focalY = (clientY == null ? rect.top + rect.height / 2 : clientY) - (rect.top + rect.height / 2);
        const clamped = this.clamp(nextScale, this.MIN, this.MAX);
        const ratio = clamped / this.scale;
        this.tx = (this.tx - focalX) * ratio + focalX;
        this.ty = (this.ty - focalY) * ratio + focalY;
        this.scale = clamped;
        this.apply();
      }

      zoomIn(clientX, clientY) {
        this.zoomTo(this.scale * this.STEP, clientX, clientY);
      }

      zoomOut(clientX, clientY) {
        this.zoomTo(this.scale / this.STEP, clientX, clientY);
      }

      onWheel(event) {
        event.preventDefault();
        this.zoomTo(this.scale * (event.deltaY < 0 ? this.STEP : 1 / this.STEP), event.clientX, event.clientY);
      }

      toggleZoom(event) {
        if (this.isZoomed) this.reset();
        else this.zoomTo(2.5, event.clientX, event.clientY);
      }

      // Desktop: a single click (not a drag) toggles zoom.
      onClick(event) {
        if (this.lastPointerType === 'touch' || this.dragged) return;
        this.toggleZoom(event);
      }

      // Mobile: double-tap toggles zoom (single click handles desktop).
      onDblClick(event) {
        event.preventDefault();
        if (this.lastPointerType !== 'touch') return;
        this.toggleZoom(event);
      }

      onPointerDown(event) {
        this.lastPointerType = event.pointerType;
        this.dragged = false;
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.pointers.size === 2) {
          const [a, b] = [...this.pointers.values()];
          this.pinchStartDistance = Math.hypot(a.x - b.x, a.y - b.y);
          this.pinchStartScale = this.scale;
          this.classList.add('is-pinching');
          this.style.touchAction = 'none';
        } else if (this.isZoomed) {
          this.setPointerCapture?.(event.pointerId);
          this.dragStart = { x: event.clientX, y: event.clientY, tx: this.tx, ty: this.ty };
          this.style.cursor = 'grabbing';
        }
      }

      onPointerMove(event) {
        const tracked = this.pointers.get(event.pointerId);
        if (!tracked) return;
        tracked.x = event.clientX;
        tracked.y = event.clientY;

        if (this.pointers.size >= 2) {
          event.preventDefault();
          const [a, b] = [...this.pointers.values()];
          const distance = Math.hypot(a.x - b.x, a.y - b.y);
          if (this.pinchStartDistance > 0) {
            this.zoomTo(
              (this.pinchStartScale * distance) / this.pinchStartDistance,
              (a.x + b.x) / 2,
              (a.y + b.y) / 2
            );
          }
        } else if (this.dragStart) {
          event.preventDefault();
          this.tx = this.dragStart.tx + (event.clientX - this.dragStart.x);
          this.ty = this.dragStart.ty + (event.clientY - this.dragStart.y);
          if (Math.abs(event.clientX - this.dragStart.x) > 3 || Math.abs(event.clientY - this.dragStart.y) > 3) {
            this.dragged = true;
          }
          this.apply();
        }
      }

      onPointerUp(event) {
        this.pointers.delete(event.pointerId);

        if (this.pointers.size < 2) {
          this.pinchStartDistance = 0;
          this.classList.remove('is-pinching');
        }
        if (this.pointers.size === 0) {
          this.dragStart = null;
          this.style.cursor = this.isZoomed ? 'grab' : '';
          if (this.scale <= 1.02) this.reset();
        }
      }
    }
  );
}
